import { useMemo } from "react";

import useFirestore from "hooks/useFirestore";
import {
  getVisibleSettingsGroups,
  getVisibleSettingsSections,
  getSettingsSection
} from "./settingsSections";
import type {
  SettingsSection,
  SettingsSectionId,
  SettingsVisibilityContext
} from "./settingsSections";
import type { SettingsCompletenessContext } from "./settingsRequirements";

/**
 * Resolves the registry's gates against the live user, once, for every host.
 *
 * The hooks are the reason this is a hook and not a plain function: `isVisible` takes a
 * SNAPSHOT (see `SettingsVisibilityContext`) precisely so the predicates stay callable
 * from anywhere, and the job of reading the contexts lands here instead.
 *
 * ⚠️ IT MUST READ ONLY `FirestoreContext`, AND MUST NEVER FETCH.
 *
 * Every host calls this on mount — including a `SettingsPanel` that is CLOSED, since a
 * hook cannot be conditional. The first version asked `useVirtualNumbers()` for
 * `hasVirtualNumbers`, which issues a forced revalidation on mount: merely rendering the
 * closed minisite-design panel in `MinisiteOwnerBar` fired a virtual-numbers request on
 * every view of a realtor's own minisite.
 *
 * `userSettings.virtualNumbers` is the same list off the user document the context is
 * already subscribed to (`PricingPlans` and four other call sites read it this way), so
 * the gate costs nothing. Keep any future gate on this footing — if a section needs data
 * a fetch would provide, gate it on something already in `userSettings` and let the
 * SECTION do the fetching once it is actually rendered.
 */
export function useSettingsVisibility(): SettingsVisibilityContext {
  const { userSettings, organizations } = useFirestore();

  return useMemo(
    () => ({
      userSettings,
      hasVirtualNumbers: (userSettings?.virtualNumbers?.length ?? 0) > 0,
      hasOrganization: (organizations?.length ?? 0) > 0
    }),
    [userSettings, organizations?.length]
  );
}

/**
 * The two documents the per-section "3 / 5" counters are evaluated against.
 *
 * Same footing as the visibility context above and for the same reason: both documents are
 * already subscribed to by `FirestoreContext`, so every requirement for every section can
 * be evaluated on render without a single request. A requirement needing anything else
 * does not belong in `settingsRequirements.ts`.
 */
export function useSettingsCompleteness(): SettingsCompletenessContext {
  const { userSettings, listingData } = useFirestore();

  return useMemo(() => ({ userSettings, listingData }), [userSettings, listingData]);
}

/** The sections this user may see, grouped, with empty groups dropped. */
export function useSettingsGroups() {
  const context = useSettingsVisibility();
  return useMemo(() => getVisibleSettingsGroups(context), [context]);
}

/** Flat list of the sections this user may see, in registry order. */
export function useVisibleSettingsSections(): SettingsSection[] {
  const context = useSettingsVisibility();
  return useMemo(() => getVisibleSettingsSections(context), [context]);
}

/**
 * One section, but only if this user is allowed it.
 *
 * Returns `null` for both "no such id" and "gated away", and the callers want exactly
 * that collapse: a deep link from a two-year-old release note and a deep link to a
 * feature the account has since lost should both land on the section list rather than on
 * an empty pane.
 */
export function useSettingsSection(id: SettingsSectionId | null | undefined): SettingsSection | null {
  const context = useSettingsVisibility();

  return useMemo(() => {
    if (!id) return null;
    const entry = getSettingsSection(id);
    if (!entry || !entry.isVisible(context)) return null;
    return entry;
  }, [id, context]);
}
